// ========================================
// Statistics Service
// Handles statistical calculations and aggregations
// ========================================

const StatisticsService = {
    /**
     * Get overall system statistics
     */
    getStatistics() {
        const professors = ProfessorService.getProfessors();
        const formations = FormationService.getFormations();
        const subjects = SubjectService.getSubjects();
        const classes = ClassService.getClasses();

        let totalWeeklyCost = 0;
        let totalWeeklyHours = 0;

        professors.forEach(prof => {
            totalWeeklyCost += ProfessorService.getProfessorWeeklyCost(prof.id);
            totalWeeklyHours += ProfessorService.getProfessorWeeklyHours(prof.id);
        });

        return {
            totalProfessors: professors.length,
            totalFormations: formations.length,
            totalSubjects: subjects.length,
            totalClasses: classes.length,
            totalWeeklyCost,
            totalWeeklyHours
        };
    },

    /**
     * Get formation type distribution
     */
    getFormationTypeDistribution() {
        const formations = FormationService.getFormations();
        const distribution = {};

        formations.forEach(formation => {
            if (!distribution[formation.type]) {
                distribution[formation.type] = 0;
            }
            distribution[formation.type]++;
        });

        return distribution;
    },

    /**
     * Get professor workload distribution
     */
    getProfessorWorkloadDistribution() {
        const professors = ProfessorService.getProfessors();

        return professors.map(prof => {
            const hours = ProfessorService.getProfessorWeeklyHours(prof.id);
            const cost = ProfessorService.getProfessorWeeklyCost(prof.id);
            const classes = ClassService.getClassesByProfessor(prof.id);

            return {
                professorId: prof.id,
                professorName: `${prof.firstName} ${prof.lastName}`,
                weeklyHours: hours,
                weeklyCost: cost,
                totalClasses: classes.length,
                workloadLevel: this.getWorkloadLevel(hours)
            };
        }).sort((a, b) => b.weeklyHours - a.weeklyHours);
    },

    /**
     * Get workload level based on hours
     */
    getWorkloadLevel(hours) {
        if (hours === 0) return 'none';
        if (hours < 10) return 'low';
        if (hours < 20) return 'medium';
        if (hours < 30) return 'high';
        return 'very-high';
    },

    /**
     * Get class distribution by day of week
     */
    getClassDistributionByDay() {
        const classes = ClassService.getClasses();
        const distribution = {
            0: 0, // Domingo
            1: 0, // Lunes
            2: 0, // Martes
            3: 0, // Miércoles
            4: 0, // Jueves
            5: 0, // Viernes
            6: 0  // Sábado
        };

        classes.forEach(cls => {
            distribution[cls.dayOfWeek]++;
        });

        return distribution;
    },

    /**
     * Get busiest time slots
     */
    getBusiestTimeSlots() {
        const classes = ClassService.getClasses();
        const timeSlots = {};

        classes.forEach(cls => {
            const hour = parseInt(cls.startTime.split(':')[0]);
            if (!timeSlots[hour]) {
                timeSlots[hour] = 0;
            }
            timeSlots[hour]++;
        });

        return Object.entries(timeSlots)
            .map(([hour, count]) => ({
                hour: parseInt(hour),
                timeSlot: `${hour}:00 - ${parseInt(hour) + 1}:00`,
                count
            }))
            .sort((a, b) => b.count - a.count);
    },

    /**
     * Get monthly cost projection
     */
    getMonthlyCostProjection() {
        const stats = this.getStatistics();
        // Assuming 4 weeks per month
        return stats.totalWeeklyCost * 4;
    },

    /**
     * Get yearly cost projection
     */
    getYearlyCostProjection() {
        const stats = this.getStatistics();
        // Assuming 48 weeks per year (accounting for holidays)
        return stats.totalWeeklyCost * 48;
    },

    /**
     * Get top professors by hours
     */
    getTopProfessorsByHours(limit = 5) {
        const workload = this.getProfessorWorkloadDistribution();
        return workload.slice(0, limit);
    },

    /**
     * Get formations with most classes
     */
    getTopFormationsByClasses(limit = 5) {
        const formations = FormationService.getFormations();

        return formations.map(formation => {
            const stats = FormationService.getFormationStats(formation.id);
            return {
                formationId: formation.id,
                formationName: formation.name,
                formationType: formation.type,
                ...stats
            };
        })
            .sort((a, b) => b.totalClasses - a.totalClasses)
            .slice(0, limit);
    },

    /**
     * Get average class duration
     */
    getAverageClassDuration() {
        const classes = ClassService.getClasses();
        if (classes.length === 0) return 0;

        let totalDuration = 0;
        classes.forEach(cls => {
            totalDuration += Utils.calculateDuration(cls.startTime, cls.endTime);
        });

        return totalDuration / classes.length;
    }
};

// Make StatisticsService globally available
window.StatisticsService = StatisticsService;
